import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { strategicKpisApi } from '../services/strategicKpisApi';
import type { ActualsResponse, KpiPersonYear, KpiSelection } from '../types/strategicKpis';
import {
    EMPTY_SELECTION, isEmptySelection, pruneSelection, readSelection, selectionKey, selectionOptions, writeSelection,
    type SelectionOptions,
} from '../utils/strategicKpiSelection';

export const BU_OPTIONS: { value: string; label: string; bus: string[] }[] = [
    { value: 'SPC', label: 'SPC', bus: ['SPC'] },
    { value: 'PLA', label: 'PLA', bus: ['PLA'] },
    { value: 'F&P', label: 'F&P', bus: ['F&P'] },
    { value: 'ALL', label: 'All BUs (F&P, PLA, SPC)', bus: ['F&P', 'PLA', 'SPC'] },
];
const DEFAULT_BU = 'SPC';

/** Today in Europe/Zurich as YYYY-MM-DD (never toISOString — feedback_toISOString_timezone). */
export function todayYmd(): string {
    return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Zurich', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
}

/**
 * The dashboard's parameters (BU, year, as-of date, K3 selection) and the actuals they select.
 * BU, year and the selection live in the URL (a filtered view is a shareable link; only
 * non-default values are written); the as-of date does not. The last successful response stays
 * on screen while the next one loads, so a changed parameter does not blank the page; a failed
 * load keeps the old data and shows the error next to it.
 *
 * K3: the persons of the year feed the three lists (cascading options). A request with a
 * selection waits for them and drops values no person of the BU holds (a year switch, a stale
 * link) — the engine would refuse those with a 400.
 */
export function useStrategicKpis(enabled: boolean) {
    const today = todayYmd();
    const thisYear = Number(today.slice(0, 4));
    const [params, setParams] = useSearchParams();

    const buParam = params.get('bu');
    const bu = BU_OPTIONS.some(o => o.value === buParam) ? buParam! : DEFAULT_BU;
    const yearParam = Number(params.get('year'));
    const year = Number.isInteger(yearParam) && yearParam >= 2000 && yearParam <= 2100 ? yearParam : thisYear;
    const urlSelKey = ['companies', 'teams', 'persons'].map(k => params.get(k) ?? '').join('|');
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const selection = useMemo(() => readSelection(params), [urlSelKey]);

    const [asOf, setAsOf] = useState<string>(today);
    const [data, setData] = useState<ActualsResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [reloadKey, setReloadKey] = useState(0);
    const [yearPersons, setYearPersons] = useState<{ year: number; rows: KpiPersonYear[] } | null>(null);
    const requestSeq = useRef(0);
    /** Set by reload(): the NEXT request bypasses the engine's cache, later ones use it again. */
    const nocacheNext = useRef(false);

    const bus = useMemo(() => (BU_OPTIONS.find(o => o.value === bu) ?? BU_OPTIONS[0]).bus, [bu]);

    const update = useCallback((fn: (p: URLSearchParams) => void) => {
        setParams(prev => { const next = new URLSearchParams(prev); fn(next); return next; }, { replace: true });
    }, [setParams]);

    /** A new BU starts without a selection — its companies, teams and persons are different ones. */
    const setBu = useCallback((v: string) => update(p => {
        if (v === DEFAULT_BU) p.delete('bu'); else p.set('bu', v);
        writeSelection(p, EMPTY_SELECTION);
    }), [update]);
    const setYear = useCallback((y: number) => update(p => { if (y === thisYear) p.delete('year'); else p.set('year', String(y)); }), [update, thisYear]);
    const setSelection = useCallback((s: KpiSelection) => update(p => writeSelection(p, s)), [update]);

    // The persons of the year: the options of the three lists, and the check of a linked selection.
    useEffect(() => {
        if (!enabled) return;
        let cancelled = false;
        strategicKpisApi.persons(year)
            .then(rows => { if (!cancelled) setYearPersons({ year, rows }); })
            // Without the list the selection goes to the server unchecked — it validates (400 with the names).
            .catch(() => { if (!cancelled) setYearPersons({ year, rows: [] }); });
        return () => { cancelled = true; };
    }, [enabled, year]);

    const personsReady = yearPersons?.year === year;
    const personRows = personsReady ? yearPersons.rows : null;

    // What is requested: no selection at once; a selection once the persons are in, pruned.
    const requestSelection: KpiSelection | null = isEmptySelection(selection) ? EMPTY_SELECTION
        : personRows === null ? null
        : personRows.length === 0 ? selection
        : pruneSelection(personRows, bus, selection);
    const requestKey = requestSelection ? selectionKey(requestSelection) : null;

    // Keep the URL honest: a dropped value leaves the link too.
    useEffect(() => {
        if (requestSelection && requestSelection !== selection && !isEmptySelection(selection)) setSelection(requestSelection);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestKey]);

    useEffect(() => {
        if (!enabled || requestSelection === null) return;
        if (!/^\d{4}-\d{2}-\d{2}$/.test(asOf)) return;   // a half-typed date is not a request
        const seq = ++requestSeq.current;
        setLoading(true);
        setError(null);
        const nocache = nocacheNext.current;
        nocacheNext.current = false;
        strategicKpisApi.actuals({ bus, year, asOf, selection: requestSelection }, nocache)
            .then(r => { if (seq === requestSeq.current) setData(r); })
            .catch(e => { if (seq === requestSeq.current) setError(e instanceof Error ? e.message : 'Failed to load the KPI actuals'); })
            .finally(() => { if (seq === requestSeq.current) setLoading(false); });
        // bus follows bu and requestSelection follows requestKey; the keys keep identities out of the deps
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, bu, year, asOf, reloadKey, requestKey]);

    /** Bypasses the engine's 5-minute cache (nocache=1). */
    const reload = useCallback(() => { nocacheNext.current = true; setReloadKey(k => k + 1); }, []);

    const options: SelectionOptions | null = useMemo(
        () => (personRows && personRows.length > 0 ? selectionOptions(personRows, bus, selection) : null),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [personRows, bu, urlSelKey]);

    return {
        bu, setBu, bus, year, setYear, asOf, setAsOf,
        selection: requestSelection ?? selection, setSelection, options,
        data, loading, error, reload,
    };
}
